import { formatDisplayName } from "../utils/textUtils";
import { formatResistance, isOpenLoop } from "../utils/sensorStatus";

const formatValue = (val: any) => {
  if (val === undefined || val === null || val === "") return "—";
  if (isOpenLoop(val)) return "OL";
  const num = parseFloat(val);
  return isNaN(num) ? val : num.toFixed(2);
};

const SensorCard = ({ sensor }: any) => {
  const getStatusStyle = (status: string) => {
    switch (status?.toLowerCase()) {
      case "active":
        return {
          bg: "rgba(16,185,129,0.10)",
          border: "rgba(16,185,129,0.25)",
          color: "#34d399",
          dot: "#10b981",
        };
      case "critical":
      case "danger":
        return {
          bg: "rgba(245,158,11,0.10)",
          border: "rgba(245,158,11,0.25)",
          color: "#fbbf24",
          dot: "#f59e0b",
        };
      case "inactive":
      default:
        return {
          bg: "rgba(239,68,68,0.10)",
          border: "rgba(239,68,68,0.20)",
          color: "#f87171",
          dot: "#ef4444",
        };
    }
  };

  const s = getStatusStyle(sensor.status);

  /* ── Animated status dot ──────────────────────────────────────────── */
  const StatusDot = ({ size = "h-2.5 w-2.5" }: { size?: string }) => (
    <span className={`relative flex ${size} flex-shrink-0`}>
      {sensor.status?.toLowerCase() === "active" && (
        <span
          className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60"
          style={{ background: s.dot }}
        />
      )}
      <span
        className="relative inline-flex rounded-full h-full w-full"
        style={{ background: s.dot }}
      />
    </span>
  );

  /* ── Status badge ─────────────────────────────────────────────────── */
  const StatusBadge = () => (
    <span
      className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full whitespace-nowrap"
      style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.color }}
    >
      {sensor.status || "Unknown"}
    </span>
  );

  /* ── Reusable metric tile for card view ───────────────────────────── */
  const MetricTile = ({
    label,
    value,
    color,
    highlight = false,
  }: {
    label: string;
    value: string;
    color: string;
    highlight?: boolean;
  }) => (
    <div
      className="rounded-xl px-3 py-2.5 flex flex-col gap-1"
      style={{
        background: highlight ? `${s.bg}` : "rgba(255,255,255,0.03)",
        border: highlight ? `1px solid ${s.border}` : "1px solid rgba(255,255,255,0.06)",
      }}
    >
      <span className="text-[9px] font-semibold uppercase tracking-widest text-slate-500">
        {label}
      </span>
      <span className="font-mono text-sm font-bold leading-none" style={{ color }}>
        {value}
      </span>
    </div>
  );

  /* ─────────────────────────────────────────────────────────────────────
     CARD VIEW  — shown on everything BELOW xl (< 1280px)
     This covers mobile (< 640px), tablet (640–1023px) and 1024px itself.
  ──────────────────────────────────────────────────────────────────── */
  const CardView = () => (
    <div className="xl:hidden px-3 sm:px-4 py-3.5 space-y-3">
      {/* Header row: dot + name + status */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <StatusDot />
          <span className="font-semibold text-white text-sm sm:text-base truncate">
            {formatDisplayName(sensor.name)}
          </span>
        </div>
        <StatusBadge />
      </div>

      {/* Metric grid:
          ≤ 474px  → 2 columns
          475px–xl → 3 columns (xs breakpoint from index.css)
      */}
      <div className="grid grid-cols-2 xs:grid-cols-3 gap-2">
        <MetricTile
          label="Resistance"
          value={formatResistance(sensor.resistance)}
          color={sensor.status === "Critical" ? "#fb7185" : "#fbbf24"}
          highlight={sensor.status === "Critical"}
        />
        <MetricTile
          label="Limit"
          value={`${
            sensor.criticalThreshold != null
              ? Number(sensor.criticalThreshold).toFixed(1)
              : "3.0"
          } Ω`}
          color="#fcd34d"
        />
        <MetricTile
          label="Ground"
          value={`${formatValue(sensor.ground)} Ω`}
          color="#c084fc"
        />
        <MetricTile
          label="Voltage"
          value={`${formatValue(sensor.voltage)} V`}
          color="#60a5fa"
        />
        <MetricTile
          label="Current"
          value={`${formatValue(sensor.current)} A`}
          color="#34d399"
        />

        {/* Maintenance tile */}
        <div
          className="rounded-xl px-3 py-2.5 flex flex-col gap-1"
          style={{
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <span className="text-[9px] font-semibold uppercase tracking-widest text-slate-500">
            Maintenance
          </span>
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] text-slate-400 leading-4">
              <span className="text-slate-600">Next: </span>
              {sensor.nextMaintenanceDate || "—"}
            </span>
            <span className="text-[10px] text-slate-500 leading-4">
              <span className="text-slate-600">Last: </span>
              {sensor.lastMaintenanceDate || "—"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  /* ─────────────────────────────────────────────────────────────────────
     TABLE ROW VIEW — shown only at xl+ (≥ 1280px)
     At this width the 280px sidebar + 8 table columns all fit comfortably.
  ──────────────────────────────────────────────────────────────────── */
  const TableRow = () => (
    <div className="hidden xl:flex items-center px-5 xl:px-6 py-3.5 gap-3 group-hover:bg-white/[0.025] transition-colors">
      {/* Name column */}
      <div className="w-44 shrink-0 flex items-center gap-2.5">
        <StatusDot size="h-2 w-2" />
        <span className="text-sm font-semibold text-slate-200 group-hover:text-white transition-colors truncate">
          {formatDisplayName(sensor.name)}
        </span>
      </div>

      {/* Resistance */}
      <div className="flex-1 flex justify-center">
        <span
          className="font-mono text-xs font-bold tabular-nums"
          style={{ color: sensor.status === "Critical" ? "#fb7185" : "#fbbf24" }}
        >
          {formatResistance(sensor.resistance)}
        </span>
      </div>

      {/* Critical Limit */}
      <div className="flex-1 flex justify-center">
        <span
          className="font-mono text-[11px] px-2.5 py-0.5 rounded-md tabular-nums"
          style={{
            background: "rgba(245,158,11,0.10)",
            border: "1px solid rgba(245,158,11,0.20)",
            color: "#fcd34d",
          }}
          title={`Site Critical Threshold: ${
            sensor.criticalThreshold != null
              ? Number(sensor.criticalThreshold).toFixed(1)
              : "3.0"
          } Ω`}
        >
          {sensor.criticalThreshold != null
            ? `${Number(sensor.criticalThreshold).toFixed(1)} Ω`
            : "3.0 Ω"}
        </span>
      </div>

      {/* Ground */}
      <div className="flex-1 flex justify-center">
        <span className="font-mono text-xs text-purple-400 tabular-nums">
          {formatValue(sensor.ground)} Ω
        </span>
      </div>

      {/* Voltage */}
      <div className="flex-1 flex justify-center">
        <span className="font-mono text-xs text-blue-400 tabular-nums">
          {formatValue(sensor.voltage)} V
        </span>
      </div>

      {/* Current */}
      <div className="flex-1 flex justify-center">
        <span className="font-mono text-xs text-emerald-400 tabular-nums">
          {formatValue(sensor.current)} A
        </span>
      </div>

      {/* Status */}
      <div className="flex-1 flex justify-center">
        <StatusBadge />
      </div>

      {/* Maintenance */}
      <div className="flex-1 flex flex-col items-center text-[10px] gap-0.5 text-center">
        <div>
          <span className="text-slate-600">Last: </span>
          <span className="text-slate-400">{sensor.lastMaintenanceDate || "—"}</span>
        </div>
        <div>
          <span className="text-slate-600">Next: </span>
          <span className="text-slate-400">{sensor.nextMaintenanceDate || "—"}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="group transition-all duration-150"
      style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}
    >
      <CardView />
      <TableRow />
    </div>
  );
};

export default SensorCard;
